// Classical Shadbala sub-components
// These implement the detailed sub-components as per BPHS and classical texts

import { norm360 } from './ayanamsa';
import { SIGN_LORDS, OWN_SIGNS, FRIENDS, ENEMIES, EXALT, DEBIL } from './astroEngine';
import type { PlanetName } from './astroEngine';
import type { PlanetFriendship } from './fiveFoldFriendship';

// Helper: get sign index 0-11 from longitude
const signOf = (lon: number) => Math.floor(norm360(lon) / 30);

// Helper: normalize sign number (accept 0-11 or 1-12, return 1-12)
function normalizeSignNum(x: any): number {
  const n = Number(x);
  if (!Number.isFinite(n)) return NaN;
  if (n >= 1 && n <= 12) return n;
  if (n >= 0 && n <= 11) return n + 1;
  return NaN;
}

/* ==================== STHAN BALA SUB-COMPONENTS ==================== */

/**
 * Saptavargaja Bala: Strength from 7 divisional charts (max 133 virupa)
 * Uses D1, D2, D3, D7, D9, D12, D30 with classical weightings
 * 
 * @param fiveFoldFriendships - Optional five-fold friendships for personalized scoring
 */
export function saptavargajaBala(
  planet: PlanetName,
  d1Lon: number,
  vargas?: any,
  fiveFoldFriendships?: PlanetFriendship[]
): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 66.5;

  const vargaKeys = ['D1', 'D2', 'D3', 'D7', 'D9', 'D12', 'D30'] as const;
  const vargaWeights = [5, 2, 3, 1.5, 4.5, 2, 4];
  
  let totalScore = 0;
  let totalWeight = 0;

  for (let i = 0; i < vargaKeys.length; i++) {
    const vk = vargaKeys[i];
    const weight = vargaWeights[i];
    
    let signNum: number;
    if (vk === 'D1') {
      signNum = signOf(d1Lon) + 1;
    } else if (vargas?.[vk]?.[planet]) {
      signNum = normalizeSignNum(vargas[vk][planet]);
    } else {
      continue;
    }

    if (!Number.isFinite(signNum) || signNum < 1 || signNum > 12) continue;

    const s = signNum - 1;
    const lord = SIGN_LORDS[s];
    const own = OWN_SIGNS[planet]?.includes(s);
    
    // Use five-fold friendships if available, otherwise fall back to natural friendships
    let friend = false;
    let enemy = false;
    
    if (fiveFoldFriendships && lord) {
      const friendship = fiveFoldFriendships.find(f => f.from === planet && f.to === lord);
      if (friendship) {
        friend = friendship.panchadha === 'friend' || friendship.panchadha === 'great_friend';
        enemy = friendship.panchadha === 'enemy' || friendship.panchadha === 'great_enemy';
      }
    } else {
      // Fallback to natural friendships
      friend = FRIENDS[planet]?.includes(lord) || false;
      enemy = ENEMIES[planet]?.includes(lord) || false;
    }

    let score = 10;
    if (own) score = 20;
    else if (friend) score = 15;
    else if (enemy) score = 7.5;

    const exSign = EXALT[planet] ? Math.floor(EXALT[planet]! / 30) : -1;
    const debSign = DEBIL[planet] ? Math.floor(DEBIL[planet]! / 30) : -1;
    if (s === exSign) score = 20;
    if (s === debSign) score = 5;

    totalScore += score * weight;
    totalWeight += weight;
  }

  const normalized = totalWeight > 0 ? (totalScore / totalWeight) * (133 / 20) : 66.5;
  return Math.max(0, Math.min(133, normalized));
}

/**
 * Ojayugmarasyamsa Bala: Odd/even sign strength (max 30 virupa)
 * Masculine planets in odd signs, feminine in even signs get full strength
 */
export function ojayugmarasyamsaBala(planet: PlanetName, lon: number): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 15;
  
  const signNum = signOf(lon) + 1;
  const isOddSign = signNum % 2 === 1;
  
  const masculine = ['Sun', 'Mars', 'Jupiter'].includes(planet);
  const feminine = ['Moon', 'Venus'].includes(planet);
  
  if ((masculine && isOddSign) || (feminine && !isOddSign)) {
    return 30;
  } else if (masculine || feminine) {
    return 0;
  } else {
    return 15;
  }
}

/**
 * Kendra Bala: Angular house strength (max 60 virupa)
 * Kendra=60, Panapara=30, Apoklima=15
 */
export function kendraBala(house: number): number {
  const kendra = [1, 4, 7, 10];
  const panapara = [2, 5, 8, 11];
  
  if (kendra.includes(house)) return 60;
  if (panapara.includes(house)) return 30;
  return 15;
}

/**
 * Drekkana Bala: Decanate strength (max 15 virupa)
 * First decanate favors masculine, third favors feminine
 */
export function drekkanaBala(planet: PlanetName, lon: number): number {
  if (planet === 'Rahu' || planet === 'Ketu') return 7.5;
  
  const signPos = norm360(lon) % 30;
  const decanate = Math.floor(signPos / 10);
  
  const masculine = ['Sun', 'Mars', 'Jupiter'].includes(planet);
  const feminine = ['Moon', 'Venus'].includes(planet);
  
  if (decanate === 0 && masculine) return 15;
  if (decanate === 2 && feminine) return 15;
  if (decanate === 1) return 10;
  return 5;
}

/* ==================== KALA BALA SUB-COMPONENTS ==================== */

/**
 * Vara Bala: Weekday lord strength (max 45 virupa)
 * Planet gets 45 if it's the lord of the weekday
 */
export function varaBala(planet: PlanetName, weekday: number): number {
  // weekday: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
  const lords: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  return lords[weekday] === planet ? 45 : 0;
}

/**
 * Hora Bala: Hour lord strength (max 60 virupa)
 * Planet gets 60 if it's the lord of the current hora
 */
export function horaBala(planet: PlanetName, date: Date, lat: number, lon: number): number {
  // Classical hora calculation requires sunrise time and hora sequence
  // Simplified: alternating sequence from day lord
  const hours = date.getUTCHours();
  const weekday = date.getUTCDay();
  const lords: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  
  // Start from day lord, cycle through the 7 planets
  const dayLordIndex = weekday;
  const horaLordIndex = (dayLordIndex + hours) % 7;
  
  return lords[horaLordIndex] === planet ? 60 : 0;
}

/**
 * Ayana Bala: Declination strength (max ~60 virupa, varies)
 * Based on Sun's declination (north/south of equator)
 */
export function ayanaBala(planet: PlanetName, sunLon: number): number {
  // Sun's declination from ecliptic longitude
  const obliquity = 23.4; // degrees
  const sunSign = Math.floor(norm360(sunLon) / 30);
  const sunSignPos = norm360(sunLon) % 30;
  
  // Simplified: declination varies from -23.4 to +23.4
  // Max at Cancer 0° (90°), min at Capricorn 0° (270°)
  const eclipticLon = norm360(sunLon);
  const declination = obliquity * Math.sin((eclipticLon - 270) * Math.PI / 180);
  
  // Northern planets (Sun, Mars, Jupiter) benefit from northern declination
  // Southern planets (Moon, Venus, Saturn) benefit from southern declination
  const northern = ['Sun', 'Mars', 'Jupiter'].includes(planet);
  const southern = ['Moon', 'Venus', 'Saturn'].includes(planet);
  
  let strength = 30; // Base
  if (northern && declination > 0) {
    strength += (declination / obliquity) * 30; // 30-60
  } else if (southern && declination < 0) {
    strength += (Math.abs(declination) / obliquity) * 30; // 30-60
  } else if (northern && declination < 0) {
    strength -= (Math.abs(declination) / obliquity) * 30; // 0-30
  } else if (southern && declination > 0) {
    strength -= (declination / obliquity) * 30; // 0-30
  }
  
  return Math.max(0, Math.min(60, strength));
}
